<template>
  <!-- Try it: the visitor switches between views of one app screen and the list changes in place.
       It never moves on its own. Every panel is in the server HTML and stacked in one cell, so
       switching never resizes the page. Tab names and column labels are the app's own strings;
       rows are sample data and stay untranslated, like other product mockups. -->
  <section class="stt">
    <div class="stt-shell stt-grid">
      <header class="stt-heading">
        <h2>{{ chapter.title }}</h2>
        <p class="stt-intro">{{ chapter.intro }}</p>
        <ul v-if="chapter.points?.length" class="stt-points">
          <li v-for="point in chapter.points" :key="point">{{ point }}</li>
        </ul>
      </header>

      <div class="stt-demo">
        <div class="stt-window saw">
          <div class="saw-head">
            <span class="saw-title">
              <span>
                <b translate="no">{{ chapter.window }}</b>
                <small v-if="chapter.windowSub" translate="no">{{ chapter.windowSub }}</small>
              </span>
            </span>
          </div>
          <div class="stt-tabs" role="group" :aria-label="chapter.tabsLabel">
            <button
              v-for="(tab, index) in chapter.tabs"
              :key="tab.label"
              type="button"
              class="stt-tab"
              :class="{ 'is-on': index === active }"
              :aria-pressed="index === active"
              translate="no"
              @click="active = index"
            >
              <i v-if="tab.dot" class="stt-dot" :class="`stt-dot--${tab.dot}`" aria-hidden="true" />{{ tab.label }}
              <span v-if="tab.count" class="stt-count">{{ tab.count }}</span>
            </button>
          </div>
          <div class="stt-panels" aria-live="polite">
            <div
              v-for="(tab, index) in chapter.tabs"
              :key="tab.label"
              class="stt-panel"
              :class="{ 'is-on': index === active }"
              :aria-hidden="index !== active"
              translate="no"
            >
              <div v-if="tab.summary" class="stt-summary">
                <span>{{ tab.summary.label }}</span>
                <b class="saw-num">{{ tab.summary.value }}</b>
              </div>
              <ul class="stt-rows">
                <li v-for="row in tab.rows" :key="row.title + row.sub">
                  <span class="stt-row-main">
                    <b>{{ row.title }}</b>
                    <small v-if="row.sub">{{ row.sub }}</small>
                  </span>
                  <em v-if="row.tag" class="saw-tag" :class="`saw-tag--${row.tag.tone}`">{{ row.tag.text }}</em>
                  <span v-if="row.value" class="saw-num stt-value">{{ row.value }}</span>
                </li>
              </ul>
              <div v-if="tab.action" class="stt-action">
                <span class="saw-muted">{{ tab.action.hint }}</span>
                <span class="saw-btn">{{ tab.action.label }}</span>
              </div>
            </div>
          </div>
        </div>
        <p v-if="chapter.note" class="stt-note">{{ chapter.note }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  // { eyebrow, title, intro, points, window, windowSub, tabsLabel, note,
  //   tabs: [{ label, dot, count, summary: { label, value }, rows: [{ title, sub, tag: { text, tone }, value }], action: { hint, label } }] }
  chapter: { type: Object, required: true },
});
const active = ref(0);
</script>

<!-- The window chrome and tokens; this component draws the window itself rather than through StoryAppWindow. -->
<style src="~/assets/css/story-app-window.css"></style>

<style scoped>
.stt { background: #fff; padding: 6.5rem 0; }
.dark .stt { background: #020617; }
.stt-shell { width: min(100% - 3rem, 84rem); margin: 0 auto; }
.stt-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .stt-grid { grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 4.5rem; } }
.stt-heading h2 { color: #020617; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.dark .stt-heading h2 { color: #f8fafc; }
.stt-intro { margin-top: 1rem; color: #475569; font-size: 1rem; line-height: 1.7; }
.dark .stt-intro { color: #cbd5e1; }
.stt-points { display: grid; gap: 0.5rem; margin-top: 1.25rem; padding: 0; list-style: none; }
.stt-points li { position: relative; padding-left: 1.1rem; color: #334155; font-size: 0.95rem; line-height: 1.55; }
.dark .stt-points li { color: #cbd5e1; }
.stt-points li::before { content: ""; position: absolute; top: 0.6em; left: 0; width: 6px; height: 6px; border-radius: 2px; background: #1ba7ce; }

.stt-window { height: auto; }
.stt-tabs { display: flex; flex-wrap: wrap; gap: 6px; border-bottom: 1px solid var(--saw-line); background: var(--saw-page); padding: 8px 12px 10px; }
.stt-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  border: 1px solid var(--saw-line);
  border-radius: 6px;
  background: var(--saw-card);
  color: var(--saw-text);
  padding: 4px 10px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.stt-tab:hover { border-color: var(--saw-muted); }
.stt-tab:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
.stt-tab.is-on { border-color: var(--saw-ink); background: var(--saw-ink); color: var(--saw-ink-text); }
.stt-count { border-radius: 4px; background: var(--saw-wash); color: var(--saw-muted); padding: 0 5px; font-size: 11px; font-variant-numeric: tabular-nums; }
.stt-tab.is-on .stt-count { background: rgb(255 255 255 / 0.18); color: inherit; }
.dark .stt-tab.is-on .stt-count { background: rgb(0 0 0 / 0.14); }
.stt-dot { width: 8px; height: 8px; border-radius: 2px; }
.stt-dot--green { background: #34a853; }
.stt-dot--amber { background: #f9ab00; }
.stt-dot--orange { background: #fa7b17; }
.stt-dot--red { background: #d93025; }
.stt-dot--blue { background: #4c6ef5; }
.stt-dot--muted { background: #9aa0a6; }

.stt-panels { display: grid; }
.stt-panel {
  display: flex;
  grid-area: 1 / 1;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.25s ease, visibility 0s linear 0.25s;
}
.stt-panel.is-on { opacity: 1; visibility: visible; transition: opacity 0.3s ease, visibility 0s; }
.stt-summary { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding: 2px 4px; color: var(--saw-muted); font-size: 12px; }
.stt-summary b { color: var(--saw-text); font-size: 18px; font-weight: 600; }
.stt-rows { display: grid; margin: 0; padding: 0; list-style: none; border: 1px solid var(--saw-line); border-radius: 10px; background: var(--saw-card); }
.stt-rows li { display: flex; align-items: center; gap: 10px; min-height: 48px; padding: 8px 12px; }
.stt-rows li + li { border-top: 1px solid var(--saw-line); }
.stt-row-main { display: grid; flex: 1; min-width: 0; line-height: 1.3; }
.stt-row-main b { overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.stt-row-main small { color: var(--saw-muted); font-size: 11.5px; }
.stt-value { min-width: 5.5em; font-weight: 600; }
.stt-action { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 0 2px; font-size: 12px; }
.stt-note { margin-top: 0.75rem; color: #64748b; font-size: 0.82rem; }
.dark .stt-note { color: #94a3b8; }
@media (max-width: 639px) {
  .stt { padding: 4.5rem 0; }
  .stt-shell { width: min(100% - 2rem, 84rem); }
  .stt-rows li { flex-wrap: wrap; row-gap: 4px; }
  .stt-row-main { flex-basis: 100%; }
  .stt-value { margin-left: auto; }
  .stt-action { flex-wrap: wrap; }
}
@media (prefers-reduced-motion: reduce) { .stt-panel { transition: none; } }
</style>
